import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import { useAuth } from '../../hooks/useAuth.js';
import { fetchFeatures } from '../../utils/features.js';
import Card from './Card.jsx';
import Button from './Button.jsx';
import AiAssist from './AiAssist.jsx';

// Bedankjes (Clubtrots): thank a member and see the thanks they received. Only while
// the pride feature is visible for this user — nothing is fetched otherwise.

const ERRORS = {
  thanks_self: 'Jezelf bedanken kan niet 😉', invalid_member: 'Kies een lid van de club.',
  message_required: 'Schrijf een kort bedankje (max. 280 tekens).', thanks_limit: 'Je hebt de afgelopen 24 uur al 10 bedankjes gestuurd.',
  thanks_same_today: 'Je hebt deze persoon de afgelopen 24 uur al bedankt.', forbidden: 'Dat mag je niet.',
};
export const thanksError = err => ERRORS[err.code] || 'Dat lukte niet.';
export const fmtThanksDate = v => (v ? new Date(`${v.replace(' ', 'T')}${v.includes('T') ? '' : 'Z'}`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '');

// The form: pick someone (unless `toUserId` is given) and write a short message
export function ThanksForm({ toUserId = null, toName = '', onSent }) {
  const [members, setMembers] = useState([]);
  const [to, setTo] = useState(toUserId || '');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { if (!toUserId) api('/api/pride/thank-members').then(setMembers).catch(() => {}); }, [toUserId]);
  async function send() {
    setBusy(true); setError(null);
    try {
      await api('/api/pride/thanks', { method: 'POST', body: JSON.stringify({ to_user_id: Number(to), message: msg }) });
      setMsg(''); setSent(true); if (!toUserId) setTo(''); onSent?.();
    } catch (err) { setError(thanksError(err)); }
    setBusy(false);
  }
  return (
    <div style="display:flex;flex-direction:column;gap:8px">
      {!toUserId && (
        <select class="form-input" value={to} onChange={e => { setTo(e.target.value); setSent(false); }} aria-label="Wie wil je bedanken?">
          <option value="">Wie wil je bedanken?</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
      )}
      <textarea class="form-input" rows={2} maxLength={280} value={msg} onInput={e => { setMsg(e.target.value); setSent(false); }}
        placeholder={toName ? `Waarvoor wil je ${toName.split(' ')[0]} bedanken?` : 'Bedankt voor…'} aria-label="Bedankje" />
      <AiAssist kind="thanks_message" value={msg} onUse={text => { setMsg(text.slice(0, 280)); setSent(false); }} />
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <Button size="sm" onClick={send} disabled={busy || !to || !msg.trim()}>🙏 Bedanken</Button>
        <span class="text-sm text-muted">{msg.length}/280</span>
        {sent && <span class="text-sm" style="color:var(--color-success,#16a34a)">Verstuurd!</span>}
      </div>
      {error && <div class="text-sm" style="color:var(--color-danger)">{error}</div>}
    </div>
  );
}

export function ThanksItem({ t, showTo = false, canDelete = false, canHide = false, onChanged }) {
  async function run(fn) { try { await fn(); onChanged?.(); } catch { /* list stays */ } }
  return (
    <div style={`padding:8px 0;border-bottom:1px solid var(--color-border);font-size:14px;min-width:0${t.hidden ? ';opacity:.6' : ''}`}>
      <div class="text-sm text-muted">
        <strong style="color:var(--color-text)">{t.from_name || 'Oud-lid'}</strong>{showTo ? <> → <strong style="color:var(--color-text)">{t.to_name || 'Oud-lid'}</strong></> : ''} · {fmtThanksDate(t.created_at)}{t.hidden ? ' · verborgen' : ''}
      </div>
      <div style="white-space:pre-line;overflow-wrap:anywhere">{t.message}</div>
      {(canHide || canDelete) && (
        <div style="display:flex;gap:10px;margin-top:2px" class="text-sm">
          {canHide && <a href="#" onClick={e => { e.preventDefault(); run(() => api(`/api/pride/thanks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ hidden: !t.hidden }) })); }}>{t.hidden ? 'Weer tonen' : 'Verbergen op mijn profiel'}</a>}
          {canDelete && <a href="#" onClick={e => { e.preventDefault(); if (confirm('Dit bedankje verwijderen?')) run(() => api(`/api/pride/thanks/${t.id}`, { method: 'DELETE' })); }}>Verwijderen</a>}
        </div>
      )}
    </div>
  );
}

// On a profile: the thanks this member received, and (on someone else's profile) a form
export default function ThanksCard({ userId, name }) {
  const auth = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [data, setData] = useState(null);
  const own = Number(userId) === Number(auth.userId);
  const load = () => api(`/api/pride/thanks?user_id=${userId}`).then(setData).catch(() => setData({ items: [] }));
  useEffect(() => {
    setData(null);
    fetchFeatures(`${auth.userId}:${auth.activeClubId}`).then(f => { setEnabled(!!f.pride); if (f.pride) load(); }).catch(() => {});
  }, [userId, auth.userId, auth.activeClubId]);
  if (!enabled) return null;
  const items = data?.items;
  return (
    <Card title="🙏 Bedankjes">
      {!own && <div style="margin-bottom:10px"><ThanksForm toUserId={userId} toName={name} onSent={load} /></div>}
      {items?.length === 0 && <div class="text-sm text-muted">{own ? 'Nog geen bedankjes ontvangen.' : `${(name || '').split(' ')[0]} heeft nog geen bedankjes. Wees de eerste!`}</div>}
      {items?.map(t => <ThanksItem key={t.id} t={t} canHide={own} canDelete={data.can_manage || Number(t.from_user_id) === Number(auth.userId)} onChanged={load} />)}
    </Card>
  );
}
